// File parsing and utility functions for various file types and formats

export interface ParsedFlashCheckFile {
  companyName: string;
  scanDate: Date;
  displayName: string;
  month: string;
  year: string;
}

/**
 * Parses filename in format: company-name_month_year.html
 * Example: acme-corp_january_2024.html
 */
export const parseFlashCheckFileName = (filename: string): ParsedFlashCheckFile | null => {
  try {
    // Remove .html extension
    const nameWithoutExtension = filename.replace(/\.html$/i, '');
    
    // Split by underscore: [company-name, month, year]
    const parts = nameWithoutExtension.split('_');
    
    if (parts.length !== 3) {
      return null;
    }
    
    const [companyNameRaw, month, year] = parts;
    
    // Format company name: replace hyphens with spaces and capitalize
    const companyName = companyNameRaw
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
    
    // Create scan date from month and year
    const monthMap: Record<string, number> = {
      january: 0, february: 1, march: 2, april: 3, may: 4, june: 5,
      july: 6, august: 7, september: 8, october: 9, november: 10, december: 11
    };
    
    const monthIndex = monthMap[month.toLowerCase()];
    if (monthIndex === undefined) {
      return null;
    }
    
    const scanDate = new Date(parseInt(year, 10), monthIndex, 1);
    
    // Create display name
    const displayName = `${companyName} - ${month.charAt(0).toUpperCase() + month.slice(1)} ${year}`;
    
    return {
      companyName,
      scanDate,
      displayName,
      month: month.charAt(0).toUpperCase() + month.slice(1),
      year
    };
  } catch (error) {
    return null;
  }
};

/**
 * Formats date for display
 */
export const formatDate = (date: Date): string => {
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

/**
 * Formats date as relative time (e.g., "2 days ago")
 */
export const formatRelativeDate = (date: Date): string => {
  const now = new Date();
  const diffInMs = now.getTime() - date.getTime();
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  if (diffInDays === 0) {
    return 'Today';
  } else if (diffInDays === 1) {
    return 'Yesterday';
  } else if (diffInDays < 7) {
    return `${diffInDays} days ago`;
  } else if (diffInDays < 30) {
    const weeks = Math.floor(diffInDays / 7);
    return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
  } else if (diffInDays < 365) {
    const months = Math.floor(diffInDays / 30);
    return `${months} month${months > 1 ? 's' : ''} ago`;
  } else {
    const years = Math.floor(diffInDays / 365);
    return `${years} year${years > 1 ? 's' : ''} ago`;
  }
};
